import * as R from 'ramda'
import {valueOfCards} from '@kapitalist/contract/ruleset'
import type {
  TLiquidatableCardData,
  TPlayerPortfolio,
  TPlayerId,
} from '@kapitalist/contract/types'
import type {TMillions} from '@kapitalist/contract/types/millions'
import type {NEList} from '@kapitalist/utils/NEList'
import {isInstance} from '@kapitalist/utils/types'
import {neighborhoodOrderMap} from '~/game/types'
import {DataBackedModel} from '~/models/base'
import CashCard from '~/models/game/Cards/CashCard'
import PropertyWildCard from '~/models/game/Cards/PropertyWildCard'
import {cardFactory, portfolioNeighborhoodFactory} from '~/models/game/factories'
import type PortfolioNeighborhood from '~/models/game/PortfolioNeighborhood'
import StandardPortfolioNeighborhood from '~/models/game/PortfolioNeighborhoods/StandardPortfolioNeighborhood'
import type {
  AnyCard,
  AnyLiquidatableCard,
  AnyPropertyCard,
  AnyValuableCard,
} from '~/models/game/types'

// Represents the cards on the table in front of each player
export default class Portfolio extends DataBackedModel<TPlayerPortfolio> {
  public liquidatedCards: AnyLiquidatableCard[]
  public neighborhoods: PortfolioNeighborhood[]
  public playerId: TPlayerId

  constructor(data: TPlayerPortfolio) {
    super(data)
    this.playerId = data.playerId
    this.neighborhoods = data.neighborhoods.map(portfolioNeighborhoodFactory)
    this.liquidatedCards = data.cashPile.map((c: TLiquidatableCardData) => {
      return cardFactory(c)
    })
  }

  public cashBalance(): TMillions {
    return valueOfCards(this.liquidatedCards)
  }

  /* cash balance + property values */
  public totalValue(): TMillions {
    return valueOfCards(this.allValuableCards())
  }

  public allCards(): AnyCard[] {
    return [
      ...this.sortedNeighborhoods()
        .map(n => [...n.properties, ...n.residences])
        .flat(1),
      ...this.liquidatedCards,
    ]
  }

  public allValuableCards(): AnyValuableCard[] {
    return this.allCards().filter(
      (c: AnyCard): c is AnyValuableCard => !(c instanceof PropertyWildCard)
    )
  }

  public allProperties(): AnyPropertyCard[] {
    return this.sortedNeighborhoods()
      .map(n => n.properties)
      .flat(1)
  }

  public completeSets(): PortfolioNeighborhood[] {
    return this.sortedNeighborhoods().filter(n => n.isCompleteSet())
  }

  public incompleteSets(): PortfolioNeighborhood[] {
    return this.sortedNeighborhoods().filter(n => !n.isCompleteSet())
  }

  public propertiesNotInSets(): AnyPropertyCard[] {
    return this.sortedNeighborhoods()
      .filter(n => !n.isCompleteSet())
      .map(pn => pn.properties)
      .flat(1)
  }

  public standardNeighborhoods = (): StandardPortfolioNeighborhood[] => {
    return this.sortedNeighborhoods().filter(
      isInstance(StandardPortfolioNeighborhood)
    )
  }

  public sortedNeighborhoods = () => {
    return R.sortWith<PortfolioNeighborhood>(
      [
        R.ascend<PortfolioNeighborhood>(
          pn => neighborhoodOrderMap.get(pn.neighborhood) ?? 0
        ),
        // complete sets first, then by ID to keep the list order stable
        R.descend(pn => pn.isCompleteSet()),
        R.ascend(pn => pn.id),
      ],
      this.neighborhoods
    )
  }

  /** Returns a list of lists of cash cards, grouped by cash value, and stable-sorted within each group. */
  public sortedCashPile = (): NEList<AnyLiquidatableCard>[] => {
    return R.groupWith<AnyLiquidatableCard>(
      (c1, c2) => c1.value === c2.value,
      R.sortWith(
        [
          R.descend<AnyLiquidatableCard>(lc => lc.value),
          R.ascend<AnyLiquidatableCard>(lc => lc instanceof CashCard),
          // just sort by card type and version to get the same order every time here
          R.ascend<AnyLiquidatableCard>(lc => lc.type),
          R.ascend<AnyLiquidatableCard>(lc => lc.version),
        ],
        this.liquidatedCards
      )
    ) as NEList<AnyLiquidatableCard>[]
  }
}
